// Purpose: Encapsulates the Monday SDK and provides a typed interface to the Monday API.
import { gql } from "graphql-request";
import mondaySdk from "monday-sdk-js";

import type { Board, BoardGroup, Item } from "@/types/common";

const monday = mondaySdk();
monday.setApiVersion("2023-10");

// Interface that every MondayService must implement.
export interface MondayService {
  getSessionToken: () => Promise<string>;
  getBoardContext: () => Promise<MondayBoardContext>;
  getMondayPlan: () => Promise<MondayPlan>;
  getAccountSlug: () => Promise<string>;
  listenToMondayContext: (callback: (context: MondayBoardContext) => void) => void;
  getBoard: (boardId: number) => Promise<Board>;
}

// The real MondayService that will be used in production.
export const mondayService: MondayService = {
  getSessionToken,
  getBoardContext,
  getMondayPlan,
  getAccountSlug,
  listenToMondayContext,
  getBoard,
};

export async function getSessionToken() {
  const response = (await monday.get("sessionToken")) as MondayGetResponse<string>;
  return response.data;
}

export async function getBoardContext() {
  const response = (await monday.get("context")) as MondayGetResponse<MondayBoardContext>;
  return response.data;
}

export async function getMondayPlan() {
  const result = (await monday.api(gql`
    query {
      account {
        plan {
          max_users
          period
          tier
          version
        }
      }
    }
  `)) as MondayGetResponse<MondayPlanResponse>;
  return result.data.account.plan;
}

// TODO: Consolidate this w/ the query above to return both the plan and the slug
export async function getAccountSlug(): Promise<string> {
  const result = (await monday.api(gql`
    query {
      me {
        account {
          slug
        }
      }
    }
  `)) as MondayGetResponse<MondaySlugResponse>;
  return result.data.me.account.slug;
}

export function listenToMondayContext(callback: (data: MondayBoardContext) => void) {
  monday.listen("context", (response) => {
    callback(response.data as MondayBoardContext);
  });
}

export async function getItemsByIds(ids: string[], limit: number) {
  const result = (await monday.api(
    `query {
            items (ids: [${ids.map((id) => `${id}`).join(",")}] limit: ${limit}) {
              id, 
              name, 
              parent_item { id }, 
              group {id, color, title, position}, 
              column_values { 
                id, value, text, 
              } 
            }
    }`
  )) as MondayGetResponse<{ items: Item[] }>;
  return result.data.items;
}

export async function getGroups(boardId: number) {
  const result = (await monday.api(
    `query {
    boards (ids: ${boardId}) {
        groups {
            id, title, color 
        }
    } 
}`
  )) as MondayGetResponse<{ boards: [{ groups: BoardGroup[] }] }>;
  const [board] = result.data.boards;
  return board.groups;
}

export async function getItemsForGroup(boardId: number, groupId: string, limit: number, cursor: string | null) {
  const boardIds = [boardId];
  const cursorPart = cursor ? `cursor: "${cursor}"` : "";
  const result = (await monday.api(
    gql`query {
      boards (ids: [${boardIds}]) {
        groups (ids: ["${groupId}"]) {
            id 
            items_page (limit: ${limit} ${cursorPart}) {
              cursor
              items {
                id
                name
              } 
           }
        } 
      } 
    }`
  )) as {
    data: {
      boards: {
        groups: {
          id: string;
          items_page: {
            cursor: string | null;
            items: Item[];
          };
        }[];
      }[];
    };
  };
  const [board] = result.data.boards;
  const [group] = board.groups;
  return { items: group.items_page.items, cursor: group.items_page.cursor };
}

export interface MondayGetResponse<T> {
  data: T;
}

export interface MondayUser {
  id: string;
  isViewOnly: boolean;
  isGuest: boolean;
  isAdmin: boolean;
}

export interface MondaySubscription {
  plan_id: string;
  renewal_date: string;
  is_trial: boolean;
  billing_period: string;
  days_left: number;
}

export interface MondayBoardContext {
  boardId: number;
  boardIds?: number[];
  boardViewId?: number;
  workspaceId: number;
  theme: ThemeEnum;
  user?: MondayUser;
  account: {
    id: string;
  };
  app: {
    id: string;
    clientId: string;
  };
  subscription?: MondaySubscription;
  appVersion: {
    versionData: VersionData;
  };
}

export enum ThemeEnum {
  LIGHT = "light",
  DARK = "dark",
  BLACK = "black",
  HACKER = "hacker_theme",
}
export enum VALIDATION_STATUS {
  ERROR = "error",
  SUCCESS = "success",
}

export function mondayThemes() {
  return [ThemeEnum.LIGHT, ThemeEnum.DARK, ThemeEnum.BLACK, ThemeEnum.HACKER];
}

export interface VersionData {
  major: number;
  minor: number;
  patch: number;
}

export interface MondayPlan {
  max_users: number;
  period: string;
  tier: string;
  version: number;
}

interface MondayPlanResponse {
  account: {
    plan: MondayPlan;
  };
}

interface MondaySlugResponse {
  me: {
    account: {
      slug: string;
    };
  };
}

export enum MondayBoardColumnType {
  TEXT = "text",
  NUMERIC = "numbers",
  COLOR = "status",
}

export interface MondayBoardColumn {
  id: string;
  title: string;
  type: MondayBoardColumnType;
  settings_str: string;
}

export interface MondayComplexity {
  reset_in_x_seconds: number;
}

export function openItemCard(itemId: string) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  monday.execute("openItemCard", { itemId, kind: "updates" }).catch(() => undefined);
}

export const getMondayComplexity = async (requestType: "query" | "mutation"): Promise<MondayComplexity> => {
  const { data } = (await monday.api(`
        ${requestType} {
            complexity {
                query
                before
                after
                reset_in_x_seconds
              }
        }
    `)) as {
    data: {
      complexity: MondayComplexity;
    };
  };

  return data.complexity;
};

export async function getBoards(limit: number, page: number) {
  const result = (await monday.api(
    `query { boards (state: active, limit: ${limit} page: ${page}, order_by: used_at){
                     id
                     name
                     type
                     workspace {
                         id
                         name
                         settings{
                           icon {
                           image
                           color
                         }
                    }
         }
    }}`
  )) as MondayGetResponse<{ boards: Board[] }>;
  const { data } = result;
  if (!data) {
    return [];
  }
  const { boards } = data;
  return boards;
}
export function valueCreatedForMonday() {
  monday
    .execute("valueCreatedForUser")
    .then(() => {})
    .catch(() => {});
}

export async function getBoard(boardId: number) {
  const query = `
    query {
      boards (ids: ${boardId}){id name}
    }
  `;
  const response = (await monday.api(query)) as MondayGetResponse<{ boards: Board[] }>;
  return response.data.boards[0];
}
